import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronRight, Phone, RefreshCw, Search, Send, Sparkles, X, Video, Wallet, Ticket, Star, CalendarDays } from "lucide-react";
import { api } from "../console/api";
import { Groupe, TitreGroupe, Retour, Vide, Badge, Pastille } from "../espace/ui";
import { iso, jourLisible, tel } from "./outils";

const PAIEMENT = {
  payee: ["payé", "#16A34A"], partielle: ["en partie payé", "#F59E0B"], envoyee: ["facture envoyée", "#2563EB"],
  generee: ["facture prête", "#64748B"], a_generer: ["pas encore facturé", "#94A3B8"], especes: ["espèces annoncées", "#F59E0B"],
  bloquee: ["facture à vérifier", "#DC2626"],
};
const JOURS_COURTS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const dateCourte = (j) => { const d = new Date(`${j}T12:00:00`); return `${JOURS_COURTS[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}`; };
const jourSeul = (j) => JOURS_COURTS[new Date(`${j}T12:00:00`).getDay()];

/* Recherche sans accents ni majuscules, mot par mot : « lea mar » trouve « Léa Martin ». */
const simple = (t) => String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const correspond = (q, ...champs) => {
  const mots = simple(q).trim().split(/\s+/).filter(Boolean);
  if (!mots.length) return true;
  const texte = simple(champs.flat().join(" "));
  return mots.every((m) => texte.includes(m));
};

/* Une adresse e-mail compte pour son nom (« lea.martin »), pas pour son domaine : sinon
   « gmail » ou « exemple » trouvait tout le monde. */
const avantArobase = (email) => String(email || "").split("@")[0];

/* Le champ de recherche des listes (même allure que l'onglet Élèves). */
const ChampRecherche = ({ valeur, onChange, placeholder, testId }) => (
  <label className="flex items-center gap-2 rounded-2xl px-3 min-h-[46px] bg-white" style={{ border: "1px solid var(--border)" }}>
    <Search size={18} className="text-[var(--muted)] shrink-0" />
    <input value={valeur} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type="text" enterKeyHint="search"
      className="flex-1 min-w-0 outline-none text-[16px] bg-transparent" data-testid={testId} />
    {valeur && (
      <button type="button" onClick={() => onChange("")} aria-label="Effacer la recherche"
        className="w-8 h-8 grid place-items-center rounded-full shrink-0" style={{ color: "var(--muted)" }}><X size={16} /></button>
    )}
  </label>
);

const Erreur = ({ children }) => (children
  ? <p className="text-[13px] rounded-2xl px-4 py-3" style={{ background: "#FEF2F2", color: "#B91C1C" }} data-testid="ecole-evenement-erreur">{children}</p>
  : null);

/* ✓ / ✗ par participant, enregistré à l'école comme l'appel des cours. `action` : un
   bouton sous le nom (« Encaissé » d'un workshop) ; `suite` : ce qui s'ouvre au toucher. */
const Participant = ({ p, etat, onEtat, verrouille, droite, action, suite, grise }) => {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div data-testid="ecole-participant" style={grise ? { background: "#F8FAFC" } : undefined}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button className="flex-1 min-w-0 text-left" onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert}>
          <span className="block text-[15px] leading-snug" style={etat === "absent" || grise ? { color: "#94A3B8" } : undefined}>
            {p.eleve}{p.age ? <span className="text-[var(--muted)]"> · {p.age} ans</span> : null}
          </span>
          {droite && <span className="flex flex-wrap gap-1.5 mt-1">{droite}</span>}
        </button>
        {!verrouille && onEtat && (
          <>
            <button onClick={() => onEtat(etat === "absent" ? "" : "absent")} aria-label="Absent" data-testid="ecole-appel-absent"
              className="w-10 h-10 rounded-full grid place-items-center shrink-0"
              style={etat === "absent" ? { background: "#DC2626", color: "#fff" } : { border: "2px solid #E2E8F0", color: "#CBD5E1" }}><X size={17} strokeWidth={2.6} /></button>
            <button onClick={() => onEtat(etat === "present" ? "" : "present")} aria-label="Présent" data-testid="ecole-appel-present"
              className="w-10 h-10 rounded-full grid place-items-center shrink-0"
              style={etat === "present" ? { background: "#16A34A", color: "#fff" } : { border: "2px solid #E2E8F0", color: "#CBD5E1" }}><Check size={18} strokeWidth={2.8} /></button>
          </>
        )}
      </div>
      {action && <div className="px-4 pb-3 -mt-1">{action}</div>}
      {ouvert && (
        <div className="px-4 pb-3 -mt-1 text-[13px] space-y-2">
          <div className="text-[var(--muted)]">{[p.responsable, p.option, p.souhaits, p.commentaire].filter(Boolean).join(" · ") || p.email}</div>
          {p.telephone && <a href={tel(p.telephone)} className="btn-ghost text-xs inline-flex"><Phone size={13} /> Appeler</a>}
          {suite}
        </div>
      )}
    </div>
  );
};

const Ecrire = ({ type, id, nom }) => {
  const [ouvert, setOuvert] = useState(false);
  const [sujet, setSujet] = useState(`${nom} — information`);
  const [texte, setTexte] = useState("");
  const [etat, setEtat] = useState("");
  const envoyer = async () => {
    try {
      const { items } = await api(`/envois/destinataires?type=${type}&id=${encodeURIComponent(id)}`);
      const emails = items.map((x) => x.email).filter(Boolean);
      if (!emails.length) { setEtat("Personne à prévenir."); return; }
      if (!window.confirm(`Envoyer à ${emails.length} famille${emails.length > 1 ? "s" : ""} ?`)) return;
      const corps = new FormData();
      corps.append("corps", JSON.stringify({ type, id, groupe: nom, sujet, message: texte, emails, espace: true }));
      await api("/envois", { method: "POST", body: corps });
      setEtat(`Envoyé à ${emails.length} famille${emails.length > 1 ? "s" : ""}.`); setTexte(""); setOuvert(false);
    } catch (e) { setEtat(e.message); }
  };
  return (
    <Groupe>
      <button onClick={() => setOuvert(!ouvert)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
        <Pastille Icone={Send} couleur="#00A8AF" />
        <span className="flex-1 text-[15px]">Écrire aux participants</span>
        <ChevronRight size={18} style={{ color: "#C4C9D0", transform: ouvert ? "rotate(90deg)" : undefined }} />
      </button>
      {ouvert && (
        <div className="p-4 space-y-2">
          <input className="input-field w-full" value={sujet} onChange={(e) => setSujet(e.target.value)} />
          <textarea className="input-field w-full" rows={4} value={texte} onChange={(e) => setTexte(e.target.value)}
            placeholder="Ex. N'oubliez pas le pique-nique et une gourde demain !" />
          <button className="btn-primary w-full justify-center" disabled={!texte.trim()} onClick={envoyer}><Send size={14} /> Envoyer</button>
        </div>
      )}
      {etat && <p className="px-4 py-3 text-[13px] text-[var(--muted)]">{etat}</p>}
    </Groupe>
  );
};

const resumeJours = (p) => (p.jours?.length ? p.jours.map(jourSeul).join(", ") : "");

/* Un stage : ses jours et, pour chaque jour, l'appel standard (✓ présent / ✗ absent), le
   même que celui des cours de la semaine, avec les seuls enfants attendus ce jour-là.
   Les jours de chaque enfant viennent de la FACTURATION (console : Facturation › Stages ›
   « Modifier ») : l'application ne les change plus et ne touche à aucune facture (école,
   09.10.2026 : « les pro-ratas, c'est au niveau facturation que ça se fait »). */
const Stage = ({ s, onRetour, jourDemande, qDepart }) => {
  const jours = s.jours?.length ? s.jours : [s.debut];
  const auj = iso(new Date());
  const [jour, setJour] = useState(jourDemande && jours.includes(jourDemande) ? jourDemande : jours.includes(auj) ? auj : (jours.find((j) => j > auj) || jours[0]));
  const [appels, setAppels] = useState(s.appels || {});
  const participants = s.participants;
  const [q, setQ] = useState(qDepart || "");
  const [erreur, setErreur] = useState("");
  const [absentsVus, setAbsentsVus] = useState(false);
  const etats = appels[jour] || {};
  // Le jour choisi reste visible dans la rangée de jours (vendredi est hors de l'écran à 390 px).
  const rangee = useRef(null);
  useEffect(() => {
    const r = rangee.current;
    const b = r?.querySelector('[aria-pressed="true"]');
    if (r && b) r.scrollLeft = Math.max(0, b.offsetLeft - (r.clientWidth - b.offsetWidth) / 2);
  }, [jour]);
  const attendu = (p) => !p.jours?.length || p.jours.includes(jour);
  const mettre = async (cle, v) => {
    const avant = appels;
    const suite = { ...etats }; if (v) suite[cle] = v; else delete suite[cle];
    setAppels({ ...appels, [jour]: suite });
    setErreur("");
    try {
      // Seul l'enfant touché part à l'école ("" = coche retirée) : deux téléphones peuvent
      // faire l'appel en même temps sans s'effacer.
      const r = await api(`/mobile/appel/${encodeURIComponent(`stage:${s.id}`)}/${jour}`, { method: "PUT", body: JSON.stringify({ etats: { [cle]: v || "" } }) });
      setAppels((a) => ({ ...a, [jour]: r.etats || suite }));
    } catch (e) {
      setAppels(avant);
      setErreur(`Appel pas enregistré : ${e.message}`);
    }
  };
  const cherche = (p) => correspond(q, p.eleve, p.responsable, avantArobase(p.email), p.telephone);
  const duJour = participants.filter(attendu);
  const pasAttendus = participants.filter((p) => !attendu(p));
  const visibles = duJour.filter(cherche);
  const absentsVisibles = pasAttendus.filter(cherche);
  const presents = duJour.filter((p) => etats[p.cle] === "present").length;
  const absents = duJour.filter((p) => etats[p.cle] === "absent").length;
  const verrouille = jour > auj;
  const badges = (p) => (
    <>
      {p.paiement && PAIEMENT[p.paiement] && <Badge couleur={PAIEMENT[p.paiement][1]}>{PAIEMENT[p.paiement][0]}</Badge>}
      {p.jours?.length > 0 && <Badge couleur="#7C3AED">{resumeJours(p)}</Badge>}
      {String(p.droit_image || "").toLowerCase().startsWith("n") && <Badge couleur="#DC2626">Pas de photo</Badge>}
      {p.commentaire && <Badge couleur="#64748B">{p.commentaire}</Badge>}
    </>
  );
  return (
    <div className="space-y-5" data-testid="ecole-stage">
      <Retour onClick={onRetour} label="Événements" />
      <div>
        {s.en_cours && <Badge couleur="#16A34A">En cours</Badge>}
        <div className="font-display text-[24px] leading-tight mt-2">{s.nom}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1">{s.dates}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Attendus ce jour", `${duJour.length}/${participants.length}`, "#0F1B2D"], ["Présents", presents, "#16A34A"], ["Absents", absents, "#DC2626"]].map(([l, n, c]) => (
          <div key={l} className="rounded-2xl bg-white py-3" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[22px] font-semibold tabular-nums" style={{ color: c }}>{n}</div>
            <div className="text-[11.5px] text-[var(--muted)]">{l}</div>
          </div>
        ))}
      </div>
      <div ref={rangee} className="relative flex gap-2 overflow-x-auto -mx-5 px-5 pb-1" style={{ scrollbarWidth: "none" }} data-testid="ecole-stage-jours-onglets">
        {jours.map((j) => (
          <button key={j} onClick={() => setJour(j)} aria-pressed={j === jour} className="px-4 min-h-[40px] rounded-full text-[13.5px] font-semibold whitespace-nowrap shrink-0"
            style={j === jour ? { background: "#0F1B2D", color: "#fff" } : { background: "#fff", border: "1px solid var(--border)" }}>
            {j === auj ? "Aujourd'hui" : dateCourte(j)}
          </button>
        ))}
      </div>
      <ChampRecherche valeur={q} onChange={setQ} placeholder="Chercher un enfant ou un parent" testId="ecole-stage-recherche" />
      <Erreur>{erreur}</Erreur>
      <section>
        <TitreGroupe>Appel · {jourLisible(jour)}</TitreGroupe>
        <Groupe testId="ecole-stage-appel">
          {visibles.length ? visibles.map((p) => (
            <Participant key={p.cle} p={p} etat={etats[p.cle] || ""} onEtat={(v) => mettre(p.cle, v)}
              verrouille={verrouille} droite={badges(p)} />
          )) : (
            <p className="px-4 py-4 text-sm text-[var(--muted)]">
              {q ? "Personne avec ce nom ce jour-là." : participants.length ? "Aucun enfant attendu ce jour-là." : "Personne d'inscrit pour l'instant."}
            </p>
          )}
        </Groupe>
        {verrouille
          ? <p className="text-[12.5px] text-[var(--muted)] mt-2 px-1">L'appel s'ouvre le jour même.</p>
          : <p className="text-[12.5px] text-[var(--muted)] mt-2 px-1">Enregistré tout de suite, visible dans la console (Liste → Stages).</p>}
      </section>
      {absentsVisibles.length > 0 && (
        <section>
          <button onClick={() => setAbsentsVus(!absentsVus)} aria-expanded={absentsVus || !!q} data-testid="ecole-stage-pas-attendus"
            className="w-full flex items-center justify-between gap-3 px-1 mb-2 text-left">
            <span className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--muted)]">Pas attendus ce jour · {absentsVisibles.length}</span>
            <ChevronRight size={18} style={{ color: "#C4C9D0", transform: absentsVus || q ? "rotate(90deg)" : undefined }} />
          </button>
          {(absentsVus || q) && (
            <Groupe>
              {absentsVisibles.map((p) => (
                <Participant key={p.cle} p={p} grise droite={badges(p)} />
              ))}
            </Groupe>
          )}
          <p className="text-[12.5px] text-[var(--muted)] mt-2 px-1" data-testid="ecole-stage-jours-facturation">
            Les jours de chaque enfant sont ceux de sa facture (console : Facturation › Stages › « Modifier »). L'application ne change ni les jours ni la facture.
          </p>
        </section>
      )}
      <Ecrire type="stages" id={s.groupe_envoi} nom={s.nom} />
    </div>
  );
};

/* Un workshop heels : qui vient, qui a payé (TWINT ou espèces au cours), l'appel. */
const Workshop = ({ titre, twint, seance, onRetour, qDepart }) => {
  const [se, setSe] = useState(seance);
  const [q, setQ] = useState(qDepart || "");
  const [erreur, setErreur] = useState("");
  const auj = iso(new Date());
  const mettre = async (cle, v) => {
    const avant = se.appel;
    const suite = { ...se.appel }; if (v) suite[cle] = v; else delete suite[cle];
    setSe((x) => ({ ...x, appel: suite }));
    setErreur("");
    try {
      const r = await api(`/mobile/appel/${encodeURIComponent(`ws:${se.id}`)}/${se.date}`, { method: "PUT", body: JSON.stringify({ etats: { [cle]: v || "" } }) });
      setSe((x) => ({ ...x, appel: r.etats || suite }));
    } catch (e) {
      setSe((x) => ({ ...x, appel: avant }));
      setErreur(`Appel pas enregistré : ${e.message}`);
    }
  };
  /* Le même geste que Facturation › Cash / espèces « Confirmer l'encaissement » et qu'À
     traiter « Payé en espèces » : payé, et la ligne sort des paiements à confirmer. */
  const encaisser = async (p) => {
    if (!window.confirm(`${p.eleve} : ${p.montant}.– reçus (espèces ou TWINT) ?`)) return;
    setErreur("");
    try {
      await api("/facturation/especes/confirmer", { method: "POST", body: JSON.stringify({ ids: [`workshop::${p.id}`] }) });
      setSe((x) => ({ ...x, participants: x.participants.map((y) => (y.id === p.id ? { ...y, paye: true, paye_le: auj } : y)) }));
    } catch (e) { setErreur(`Encaissement pas enregistré : ${e.message}`); }
  };
  const annulerEncaissement = async (p) => {
    if (!window.confirm(`Annuler l'encaissement de ${p.eleve} ?`)) return;
    setErreur("");
    try {
      await api(`/workshops/${encodeURIComponent(p.id)}/paiement`, { method: "POST", body: JSON.stringify({ paye: false }) });
      setSe((x) => ({ ...x, participants: x.participants.map((y) => (y.id === p.id ? { ...y, paye: false, paye_le: "" } : y)) }));
    } catch (e) { setErreur(e.message); }
  };
  const payes = se.participants.filter((p) => p.paye).length;
  const presents = se.participants.filter((p) => se.appel[p.cle] === "present").length;
  const visibles = se.participants.filter((p) => correspond(q, p.eleve, avantArobase(p.email), p.telephone));
  return (
    <div className="space-y-5" data-testid="ecole-workshop">
      <Retour onClick={onRetour} label="Événements" />
      <div>
        <div className="text-[12.5px] font-semibold uppercase tracking-[0.1em]" style={{ color: "#EC4899" }}>{titre}</div>
        <div className="font-display text-[24px] leading-tight mt-1 first-letter:uppercase">{jourLisible(se.date)}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1">{se.debut}–{se.fin}{se.video ? " · workshop et vidéo" : ""} · CHF {se.prix}.– {twint ? `· TWINT ${twint}` : ""}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Inscrites", se.participants.length, "#0F1B2D"], ["Payé", `${payes}/${se.participants.length}`, "#16A34A"], ["Présentes", presents, "#00A8AF"]].map(([l, n, c]) => (
          <div key={l} className="rounded-2xl bg-white py-3" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[22px] font-semibold tabular-nums" style={{ color: c }}>{n}</div>
            <div className="text-[11.5px] text-[var(--muted)]">{l}</div>
          </div>
        ))}
      </div>
      {se.participants.length > 4 && <ChampRecherche valeur={q} onChange={setQ} placeholder="Chercher une participante" testId="ecole-ws-recherche" />}
      <Erreur>{erreur}</Erreur>
      <section>
        <TitreGroupe>Appel et paiements</TitreGroupe>
        <Groupe testId="ecole-ws-appel">
          {visibles.length ? visibles.map((p) => (
            <Participant key={p.cle} p={p} etat={se.appel[p.cle] || ""} onEtat={(v) => mettre(p.cle, v)}
              verrouille={se.date > auj}
              droite={<>
                {p.forfait && <Badge couleur="#9333EA">Forfait</Badge>}
                {p.paye
                  ? <Badge couleur="#16A34A">Payé{p.paye_le ? ` le ${p.paye_le.split("-").reverse().slice(0, 2).join(".")}` : ""}</Badge>
                  : <Badge couleur="#F59E0B">À payer · {p.montant}.–</Badge>}
              </>}
              action={!p.paye && (
                <button onClick={() => encaisser(p)} data-testid="ecole-ws-encaisse"
                  className="w-full min-h-[40px] rounded-xl text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5"
                  style={{ background: "#FEF3C7", color: "#92400E" }}>
                  <Wallet size={14} /> Encaissé (espèces/TWINT) · {p.montant}.–
                </button>
              )}
              suite={p.paye && (
                <button onClick={() => annulerEncaissement(p)} className="text-[12.5px] underline text-[var(--muted)]" data-testid="ecole-ws-annuler-encaissement">
                  Annuler l'encaissement (erreur)
                </button>
              )} />
          )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">{q ? "Personne avec ce nom." : "Personne d'inscrit pour l'instant."}</p>}
        </Groupe>
        <p className="text-[12.5px] text-[var(--muted)] mt-2 px-1">
          {se.date > auj ? "L'appel s'ouvre le jour même. " : ""}« Encaissé » = le même geste que Facturation › Cash : la ligne sort des paiements en espèces à confirmer.
        </p>
      </section>
      <Ecrire type="workshops" id="" nom={titre} />
    </div>
  );
};

/* Une audition ou un spectacle : la liste de la console, au format téléphone. */
const ListeGenerique = ({ type, id, onRetour, qDepart }) => {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [q, setQ] = useState(qDepart || "");
  useEffect(() => {
    api(`/liste/${type}/fiche?id=${encodeURIComponent(id)}`).then(setD).catch((e) => setErreur(e.message));
  }, [type, id]);
  if (erreur) return <><Retour onClick={onRetour} label="Événements" /><Vide>{erreur}</Vide></>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const autres = d.colonnes.map((c, k) => [c, d.entetes[k]]).filter(([c]) => !["prenom", "nom", "telephone", "email"].includes(c));
  // Tout le contenu de la ligne : le nom de l'enfant, celui du parent, l'adresse, le téléphone.
  const visibles = d.lignes.filter((l) => correspond(q, Object.entries(l)
    .filter(([, v]) => typeof v === "string" || typeof v === "number")
    .map(([k, v]) => (k === "email" ? avantArobase(v) : v))));
  return (
    <div className="space-y-5" data-testid="ecole-liste-generique">
      <Retour onClick={onRetour} label="Événements" />
      <div>
        <div className="text-[12.5px] font-semibold uppercase tracking-[0.1em]" style={{ color: type === "spectacles" ? "#9333EA" : "#2563EB" }}>
          {type === "spectacles" ? "Spectacle" : "Audition"}
        </div>
        <div className="font-display text-[24px] leading-tight mt-1">{d.entete.titre}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1">{[d.entete.sous_titre, d.entete.places].filter(Boolean).join(" · ")}</div>
      </div>
      <ChampRecherche valeur={q} onChange={setQ} placeholder={type === "spectacles" ? "Chercher un nom, une adresse" : "Chercher un enfant ou un parent"} testId="ecole-liste-recherche" />
      {q && <p className="text-[12.5px] font-semibold px-1 -mt-3" style={{ color: "var(--primary)" }}>{visibles.length} sur {d.lignes.length}</p>}
      <Groupe>
        {visibles.length ? visibles.map((l, n) => {
          const hors = /^(annul|doublon)/i.test(String(l.statut || ""));
          return (
            <div key={l.id || n} className="flex items-center gap-3 px-4 py-3" style={hors ? { opacity: 0.45 } : undefined} data-testid="ecole-liste-ligne">
              <div className="min-w-0 flex-1">
                <div className="text-[15px]">{l.prenom} {l.nom}</div>
                <div className="text-[12.5px] text-[var(--muted)]">
                  {autres.map(([c, e]) => (l[c] !== undefined && l[c] !== "" && l[c] !== null ? `${e} : ${l[c]}` : "")).filter(Boolean).join(" · ")}
                </div>
                {l.statut && <span className="inline-block mt-1"><Badge couleur={hors ? "#94A3B8" : "#0F1B2D"}>{l.statut}</Badge></span>}
              </div>
              {l.telephone && <a href={tel(l.telephone)} aria-label="Appeler" className="w-10 h-10 grid place-items-center rounded-full shrink-0"
                style={{ background: "var(--primary-tint)", color: "var(--primary)" }}><Phone size={16} /></a>}
            </div>
          );
        }) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Personne avec ce nom.</p>}
      </Groupe>
      <Ecrire type={type} id={id} nom={d.entete.titre} />
    </div>
  );
};

/* La recherche de la liste des événements : un enfant ou un parent, dans tous les stages,
   workshops, auditions et spectacles à venir (école, 09.10.2026 : « dans les stages -
   audition - spectacles de Noël, peut-on avoir un menu de recherche ? »). */
const resultats = (q, d, listes) => {
  const out = [];
  (d?.stages || []).forEach((s) => s.participants.forEach((p) => {
    if (correspond(q, p.eleve, p.responsable, avantArobase(p.email), p.telephone)) {
      out.push({ cle: `st-${s.id}-${p.cle}`, nom: p.eleve, ou: `Stage · ${s.nom}`, detail: [p.responsable, resumeJours(p)].filter(Boolean).join(" · "), Icone: Sparkles, couleur: "#F59E0B", ouvrir: { id: s.id } });
    }
  }));
  (d?.workshops?.seances || []).forEach((se) => se.participants.forEach((p) => {
    if (correspond(q, p.eleve, avantArobase(p.email), p.telephone)) {
      out.push({ cle: `ws-${se.id}-${p.cle}`, nom: p.eleve, ou: `Workshop · ${jourLisible(se.date)}`, detail: p.paye ? "payé" : "à payer", Icone: se.video ? Video : CalendarDays, couleur: "#EC4899", ouvrir: { id: se.id } });
    }
  }));
  [["auditions", "Audition", Star, "#2563EB"], ["spectacles", "Spectacle", Ticket, "#9333EA"]].forEach(([type, mot, Icone, couleur]) => {
    (listes[type] || []).forEach((it) => (it.personnes || []).forEach((p) => {
      if (correspond(q, p.prenom, p.nom, p.responsable, avantArobase(p.email), p.telephone)) {
        out.push({ cle: `${type}-${it.id}-${p.id}`, nom: `${p.prenom} ${p.nom}`.trim() || p.email, ou: `${mot} · ${it.nom}`,
          detail: [p.responsable, p.nb_personnes ? `${p.nb_personnes} place${p.nb_personnes > 1 ? "s" : ""}` : "", p.statut].filter(Boolean).join(" · "),
          Icone, couleur, ouvrir: { type, id: it.id }, hors: /^(annul|doublon)/i.test(String(p.statut || "")) });
      }
    }));
  });
  return out;
};

/* La liste : stages des vacances, workshops heels, auditions et spectacles à venir. */
export const Evenements = ({ cible, onDetail }) => {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [ouvert, setOuvert] = useState(cible?.id ? { id: cible.id, jour: cible.jour, type: cible.type } : null);
  const [listes, setListes] = useState({ auditions: [], spectacles: [] });
  const [q, setQ] = useState("");
  const charger = () => {
    api("/mobile/evenements").then(setD).catch((e) => setErreur(e.message));
    api("/mobile/listes").then(setListes).catch(() => {});
  };
  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onDetail?.(!!ouvert); return () => onDetail?.(false); }, [ouvert, onDetail]);
  if (erreur) return <Vide>{erreur}</Vide>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const fermer = () => { setOuvert(null); charger(); };
  // Ouvert depuis la recherche : la fiche s'ouvre avec la même recherche.
  const qDepart = ouvert?.q || "";
  if (ouvert?.type) return <ListeGenerique type={ouvert.type} id={ouvert.id} onRetour={fermer} qDepart={qDepart} />;
  if (ouvert) {
    const s = d.stages.find((x) => x.id === ouvert.id);
    if (s) return <Stage s={s} jourDemande={ouvert.jour} onRetour={fermer} qDepart={qDepart} />;
    const se = d.workshops.seances.find((x) => x.id === ouvert.id);
    if (se) return <Workshop titre={d.workshops.titre} twint={d.workshops.twint} seance={se} onRetour={fermer} qDepart={qDepart} />;
  }
  const trouves = q.trim().length >= 2 ? resultats(q, d, listes) : null;
  return (
    <div className="space-y-6" data-testid="ecole-evenements">
      <div className="space-y-2">
        <ChampRecherche valeur={q} onChange={setQ} placeholder="Chercher un enfant ou un parent" testId="ecole-evenements-recherche" />
        {!trouves && <p className="text-[12.5px] text-[var(--muted)] px-1">Dans les stages, workshops, auditions et spectacles à venir.</p>}
      </div>
      {trouves ? (
        <section data-testid="ecole-evenements-resultats">
          <TitreGroupe>{trouves.length ? `${trouves.length} résultat${trouves.length > 1 ? "s" : ""}` : "Résultats"}</TitreGroupe>
          <Groupe>
            {trouves.length ? trouves.map((r) => (
              <button key={r.cle} onClick={() => setOuvert({ ...r.ouvrir, q })} data-testid="ecole-evenements-resultat"
                className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#F1F5F9]" style={r.hors ? { opacity: 0.5 } : undefined}>
                <Pastille Icone={r.Icone} couleur={r.couleur} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold leading-snug">{r.nom}</span>
                  <span className="block text-[12.5px] text-[var(--muted)] leading-snug">{r.ou}</span>
                  {r.detail && <span className="block text-[12px] text-[var(--muted)] leading-snug">{r.detail}</span>}
                </span>
                <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
              </button>
            )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Personne avec ce nom dans les événements à venir.</p>}
          </Groupe>
        </section>
      ) : (
        <>
          <section>
            <TitreGroupe>Stages des vacances</TitreGroupe>
            <Groupe>
              {d.stages.length ? d.stages.map((s) => (
                <button key={s.id} onClick={() => setOuvert({ id: s.id })} data-testid={`ecole-stage-${s.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
                  <Pastille Icone={Sparkles} couleur="#F59E0B" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold leading-snug">{s.nom}</span>
                    <span className="block text-[12.5px] text-[var(--muted)]">{s.dates} · {s.participants.length} inscrit{s.participants.length > 1 ? "s" : ""}</span>
                  </span>
                  {s.en_cours && <Badge couleur="#16A34A">En cours</Badge>}
                  <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
                </button>
              )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Aucun stage à venir.</p>}
            </Groupe>
          </section>
          <section>
            <TitreGroupe>{d.workshops.titre}</TitreGroupe>
            <Groupe>
              {d.workshops.seances.length ? d.workshops.seances.map((se) => {
                const aPayer = se.participants.filter((p) => !p.paye).length;
                return (
                  <button key={se.id} onClick={() => setOuvert({ id: se.id })} data-testid={`ecole-ws-${se.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
                    <Pastille Icone={se.video ? Video : CalendarDays} couleur="#EC4899" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold first-letter:uppercase">{jourLisible(se.date)}</span>
                      <span className="block text-[12.5px] text-[var(--muted)]">{se.debut}–{se.fin}{se.video ? " · avec vidéo" : ""} · {se.participants.length} inscrite{se.participants.length > 1 ? "s" : ""}</span>
                    </span>
                    {aPayer > 0 && <Badge couleur="#F59E0B">{aPayer} à payer</Badge>}
                    <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
                  </button>
                );
              }) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Aucun workshop à venir.</p>}
            </Groupe>
          </section>
          {[["auditions", "Auditions", Star, "#2563EB"], ["spectacles", "Spectacles", Ticket, "#9333EA"]].map(([type, titre, Icone, couleur]) => (
            <section key={type}>
              <TitreGroupe>{titre}</TitreGroupe>
              <Groupe>
                {(listes[type] || []).length ? listes[type].map((it) => (
                  <button key={it.id} onClick={() => setOuvert({ type, id: it.id })} data-testid={`ecole-${type}-${it.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
                    <Pastille Icone={Icone} couleur={couleur} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold leading-snug">{it.nom}</span>
                      <span className="block text-[12.5px] text-[var(--muted)]">
                        {[it.date ? jourLisible(it.date) : it.quand, `${it.nb_inscrits} inscrit${it.nb_inscrits > 1 ? "s" : ""}`,
                          it.nb_personnes ? `${it.nb_personnes} places` : ""].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
                  </button>
                )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Rien à venir.</p>}
              </Groupe>
            </section>
          ))}
        </>
      )}
    </div>
  );
};
